import ClickAwayListener from "@mui/material/ClickAwayListener";
import Grow from "@mui/material/Grow";
import Paper from "@mui/material/Paper";
import Popper from "@mui/material/Popper";
import { grey } from "@mui/material/colors";
import type { PaperProps } from "@mui/material/Paper";
import type { SxProps, Theme } from "@mui/material/styles";
import type { Options } from "@popperjs/core";
import type { FC, ReactNode } from "react";
import { useEffect, useLayoutEffect, useState } from "react";

/** Default chrome for the suggestion detail paper (explainability, ARAS, etc.). */
export const suggestionDetailPopoverPaperSx: SxProps<Theme> = {
  borderRadius: "0.5rem",
  maxWidth: "26rem",
  minWidth: "17.5rem",
  boxShadow: "0px 4px 12px rgba(0, 0, 0, 0.12)",
  border: `1px solid ${grey[200]}`,
  overflowY: "auto",
  overflowX: "hidden",
  pointerEvents: "auto",
};

/** Max width of ARAS score popover paper (design spec). */
export const ARAS_POPOVER_PAPER_MAX_WIDTH = "268px";

export const arasSuggestionDetailPopoverShellSx: SxProps<Theme> = {
  maxWidth: ARAS_POPOVER_PAPER_MAX_WIDTH,
  minWidth: 0,
};

const POPOVER_VIEWPORT_PADDING = 12;
const POPOVER_REFERENCE_OFFSET = 8;

/**
 * Returns the larger of the spaces above and below the anchor — flip picks
 * the side with more room, so a popover at this max-height is guaranteed to
 * fit on its chosen side without preventOverflow having to shift it.
 */
function computeMaxHeightPx(anchor: HTMLElement): number {
  const rect = anchor.getBoundingClientRect();
  const viewportHeight = window.innerHeight;
  const above =
    rect.top - POPOVER_REFERENCE_OFFSET - POPOVER_VIEWPORT_PADDING;
  const below =
    viewportHeight -
    rect.bottom -
    POPOVER_REFERENCE_OFFSET -
    POPOVER_VIEWPORT_PADDING;
  return Math.max(0, Math.floor(Math.max(above, below)));
}

const popperModifiers: NonNullable<Options["modifiers"]> = [
  {
    name: "offset",
    enabled: true,
    options: {
      offset: [0, POPOVER_REFERENCE_OFFSET],
    },
  },
  {
    name: "flip",
    enabled: true,
    options: {
      padding: POPOVER_VIEWPORT_PADDING,
      fallbackPlacements: ["top-start", "top-end", "bottom-end"],
    },
  },
  {
    name: "preventOverflow",
    enabled: true,
    options: {
      boundary: "viewport",
      padding: POPOVER_VIEWPORT_PADDING,
      altAxis: true,
    },
  },
];

export interface SuggestionDetailPopoverShellProps {
  open: boolean;
  anchorEl: HTMLElement | null;
  onClose: () => void;
  children: ReactNode;
  /** Merged into Paper `sx` after defaults (e.g. {@link arasSuggestionDetailPopoverShellSx}). */
  sx?: SxProps<Theme>;
  /** Hover bridge: keep the popover open while the pointer moves from the anchor onto the paper. */
  paperProps?: Partial<PaperProps>;
}

/**
 * Popper-based shell: prefers below the anchor, falls back above when vertical
 * space is tight. Paper max-height is measured from the anchor BEFORE Popper
 * mounts (see {@link computeMaxHeightPx}) so popper.js sees a stable size
 * from its first pass — no resize-observer feedback loop, no placement
 * thrashing. Root uses `pointer-events: none` so anchors stay hoverable;
 * Paper uses `auto`. Click-away / Esc dismiss; consumers own the open trigger.
 */
const SuggestionDetailPopoverShell: FC<SuggestionDetailPopoverShellProps> = ({
  open,
  anchorEl,
  onClose,
  children,
  sx: sxProp,
  paperProps,
}) => {
  const { sx: paperSx, ...paperRest } = paperProps ?? {};

  useEffect(() => {
    if (!open) return undefined;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        onClose();
      }
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [open, onClose]);

  // Compute max-height in React state, not in a popper modifier. Doing it
  // through a popper modifier creates a feedback loop: the modifier shrinks
  // the Paper, ResizeObserver fires, popper re-runs, flip can pick a
  // different placement, the modifier writes a new value, and that loop
  // produces position flicker on narrow viewports (the user's bug report).
  //
  // Here `maxHeightPx` is set BEFORE we mount the Popper, so popper.js sees
  // the constrained size from its very first pass and never has to recompute.
  // We use `max(above, below)` because flip will pick whichever side has
  // more room — and at that max-height the popover is guaranteed to fit on
  // its chosen side without preventOverflow shifting it.
  const [maxHeightPx, setMaxHeightPx] = useState<number | null>(null);

  useLayoutEffect(() => {
    if (!open || !anchorEl) {
      setMaxHeightPx(null);
      return undefined;
    }
    const update = () => setMaxHeightPx(computeMaxHeightPx(anchorEl));
    update();
    window.addEventListener("resize", update);
    window.addEventListener("scroll", update, true);
    return () => {
      window.removeEventListener("resize", update);
      window.removeEventListener("scroll", update, true);
    };
  }, [open, anchorEl]);

  // Gate Popper render until we've measured the anchor. The first render
  // with open=true returns null, the layout effect runs synchronously before
  // paint and sets maxHeightPx, then a second render mounts Popper with the
  // constrained Paper size. Both renders happen in the same paint, so the
  // user only ever sees the final placement.
  const resolvedOpen = open && anchorEl != null && maxHeightPx != null;

  if (!resolvedOpen) {
    return null;
  }

  return (
    <Popper
      open
      anchorEl={anchorEl}
      placement="bottom-start"
      transition
      modifiers={popperModifiers}
      popperOptions={{
        strategy: "fixed",
      }}
      sx={{
        zIndex: (theme) => theme.zIndex.modal,
        pointerEvents: "none",
      }}
    >
      {({ TransitionProps }) => (
        <ClickAwayListener onClickAway={onClose}>
          <Grow {...TransitionProps} timeout={220}>
            <Paper
              elevation={2}
              {...paperRest}
              sx={[
                suggestionDetailPopoverPaperSx,
                { maxHeight: `${maxHeightPx}px` },
                ...(sxProp != null
                  ? Array.isArray(sxProp)
                    ? sxProp
                    : [sxProp]
                  : []),
                ...(paperSx != null
                  ? Array.isArray(paperSx)
                    ? paperSx
                    : [paperSx]
                  : []),
              ]}
            >
              {children}
            </Paper>
          </Grow>
        </ClickAwayListener>
      )}
    </Popper>
  );
};

export default SuggestionDetailPopoverShell;
